import { Routes } from '@angular/router';
import { redirectLoggedInTo, redirectUnauthorizedTo } from './guards/auth.guard';
import {
  redirectToAppIfOrgLoaded,
  requireAccordAccess,
  requireAccordStaff,
  requireOrg,
  requirePermission,
} from './guards/permission.guard';

export const routes: Routes = [
  {
    // Login/signup are centralized in the external auth portal via
    // @crewdle/auth-bridge; these paths only exist as handoff targets.
    path: 'login',
    canActivate: [redirectLoggedInTo('/dashboard')],
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
  },
  {
    path: 'signup',
    canActivate: [redirectLoggedInTo('/dashboard')],
    loadComponent: () =>
      import('./pages/auth-handoff/auth-handoff.component').then((m) => m.AuthHandoffComponent),
  },
  {
    // Neutral interstitial during global sign-out (no guard: shown while the
    // session is being torn down). See SigningOutComponent.
    path: 'signing-out',
    loadComponent: () =>
      import('./pages/signing-out/signing-out.component').then((m) => m.SigningOutComponent),
  },
  {
    // Sales-provisioned org activation (license token). Requires a session but
    // no org yet — accepting is what grants the membership.
    path: 'accept-license',
    canActivate: [redirectUnauthorizedTo('/login')],
    loadComponent: () =>
      import('./pages/accept-license/accept-license.component').then(
        (m) => m.AcceptLicenseComponent,
      ),
  },
  {
    // Authenticated but org-less / unentitled states — outside the main layout.
    path: 'no-organization',
    canActivate: [redirectUnauthorizedTo('/login'), redirectToAppIfOrgLoaded()],
    loadComponent: () =>
      import('./pages/no-organization/no-organization.component').then(
        (m) => m.NoOrganizationComponent,
      ),
  },
  {
    path: 'no-access',
    canActivate: [redirectUnauthorizedTo('/login'), redirectToAppIfOrgLoaded()],
    loadComponent: () =>
      import('./pages/no-access/no-access.component').then((m) => m.NoAccessComponent),
  },
  {
    // Staff app (punch clock + messaging) — outside the main layout. Staff
    // roles only carry 'accord.staff', deliberately not 'accord.read'.
    path: 'staff',
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg(), requireAccordStaff()],
    loadComponent: () =>
      import('./layouts/staff-layout.component').then((m) => m.StaffLayoutComponent),
    children: [
      { path: '', redirectTo: 'punch', pathMatch: 'full' },
      {
        path: 'punch',
        loadComponent: () =>
          import('./pages/staff/punch.component').then((m) => m.PunchComponent),
      },
      {
        path: 'messages',
        loadComponent: () =>
          import('./pages/staff/messages.component').then((m) => m.StaffMessagesComponent),
      },
    ],
  },
  {
    path: '',
    canActivate: [redirectUnauthorizedTo('/login'), requireOrg(), requireAccordAccess()],
    loadComponent: () => import('./layouts/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },
      {
        path: 'agent',
        canActivate: [requirePermission('accord.manage')],
        loadComponent: () =>
          import('./pages/agent/agent.component').then((m) => m.AgentComponent),
      },
      {
        path: 'schedule',
        canActivate: [requirePermission('accord.read')],
        loadComponent: () =>
          import('./pages/schedule/schedule.component').then((m) => m.ScheduleComponent),
      },
      {
        path: 'messages',
        canActivate: [requirePermission('accord.read')],
        loadComponent: () =>
          import('./pages/messages/messages.component').then((m) => m.MessagesComponent),
      },
      {
        path: 'inventory',
        canActivate: [requirePermission('accord.read')],
        loadComponent: () =>
          import('./pages/inventory/inventory.component').then((m) => m.InventoryComponent),
      },
      {
        path: 'reports',
        canActivate: [requirePermission('accord.manage')],
        loadComponent: () =>
          import('./pages/reports/reports.component').then((m) => m.ReportsComponent),
      },
      {
        path: 'team',
        canActivate: [requirePermission('members.read')],
        loadComponent: () => import('./pages/team/team.component').then((m) => m.TeamComponent),
      },
      {
        // Not in the nav — reached from the dashboard banner / direct URL.
        path: 'onboarding',
        canActivate: [requirePermission('accord.manage')],
        loadComponent: () =>
          import('./pages/onboarding/onboarding.component').then((m) => m.OnboardingComponent),
      },
      {
        path: 'settings',
        canActivate: [requirePermission('accord.manage')],
        loadComponent: () =>
          import('./pages/settings/settings.component').then((m) => m.SettingsComponent),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
